/** @jsxImportSource preact */

// Deep import: the package barrel pulls in the Pipedrive SDK and a node-only logger,
// which must stay out of the browser bundle. fields.ts itself is dependency-free.
import { ETICKET_UPLOADED } from '@plt/pipedrive-client/src/fields'

// Shared with the backend, which maps these labels back to Pipedrive option ids
// via `.from()` (see create-handlers) — importing them means they can never drift.
export const ETICKET_UPLOADED_OPTIONS = ETICKET_UPLOADED.labels

export interface AirlineDirectFormState {
  airlineReference: string
  eticketUploaded: string
  ticketingDate: string
  netFareCost: string
  grossFareCost: string
}

export const emptyAirlineDirectForm = (): AirlineDirectFormState => ({
  airlineReference: '',
  eticketUploaded: '',
  ticketingDate: '',
  netFareCost: '',
  grossFareCost: '',
})

interface AirlineDirectFieldsProps {
  value: AirlineDirectFormState
  currencyCode?: string
  onChange: (patch: Partial<AirlineDirectFormState>) => void
}

export function AirlineDirectFields({ value, currencyCode, onChange }: AirlineDirectFieldsProps) {
  const moneyInput = (label: string, field: 'netFareCost' | 'grossFareCost') => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="flex rounded-md shadow-sm">
        {currencyCode && (
          <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
            {currencyCode}
          </span>
        )}
        <input
          type="number"
          step="any"
          value={value[field]}
          onChange={e => onChange({ [field]: (e.target as HTMLInputElement).value })}
          className={`flex-1 px-3 py-2 border border-gray-300 ${currencyCode ? 'rounded-r-md' : 'rounded-md'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
          placeholder="e.g. 1234.56"
          required
        />
      </div>
    </div>
  )

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Airline Reference <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={value.airlineReference}
          onChange={e => onChange({ airlineReference: (e.target as HTMLInputElement).value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Airline confirmation / reference"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          E-ticket Uploaded <span className="text-red-500">*</span>
        </label>
        <select
          value={value.eticketUploaded}
          onChange={e => onChange({ eticketUploaded: (e.target as HTMLSelectElement).value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          required
        >
          <option value="">Select an option</option>
          {ETICKET_UPLOADED_OPTIONS.map(option => (
            <option
              key={option}
              value={option}
            >
              {option}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Ticketing Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={value.ticketingDate}
          onChange={e => onChange({ ticketingDate: (e.target as HTMLInputElement).value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required
        />
      </div>

      {moneyInput('Net Fare Cost', 'netFareCost')}
      {moneyInput('Gross Fare Cost', 'grossFareCost')}
    </>
  )
}
